import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { Moon, Sun, Monitor, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useLanguage } from '../../contexts/LanguageContext';

const translations = {
  light: { 'pt-BR': 'Claro', 'en-US': 'Light' },
  dark: { 'pt-BR': 'Escuro', 'en-US': 'Dark' },
  system: { 'pt-BR': 'Sistema', 'en-US': 'System' },
  theme: { 'pt-BR': 'Tema', 'en-US': 'Theme' },
};

export const ThemeToggle = ({ className = '', variant = 'icon' }) => {
  const { theme, themePreference, setTheme } = useTheme();
  const { t } = useLanguage();
  
  const getIcon = () => {
    if (themePreference === 'system') {
      return <Monitor className="w-5 h-5" />;
    }
    return theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />;
  };

  // Menu variant - renders as inline buttons for use inside dropdown menus
  if (variant === 'menu') {
    return (
      <div className="flex flex-wrap gap-2">
        <Button
          variant={themePreference === 'light' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTheme('light')}
          className={`rounded-full text-xs ${themePreference === 'light' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
          data-testid="theme-light-menu"
        >
          <Sun className="w-3 h-3 mr-1" />
          {t(translations.light)}
        </Button>
        <Button
          variant={themePreference === 'dark' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTheme('dark')}
          className={`rounded-full text-xs ${themePreference === 'dark' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
          data-testid="theme-dark-menu"
        >
          <Moon className="w-3 h-3 mr-1" />
          {t(translations.dark)}
        </Button>
        <Button
          variant={themePreference === 'system' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setTheme('system')}
          className={`rounded-full text-xs ${themePreference === 'system' ? 'bg-orange-500 hover:bg-orange-600' : ''}`}
          data-testid="theme-system-menu"
        >
          <Monitor className="w-3 h-3 mr-1" />
          {t(translations.system)}
        </Button>
      </div>
    );
  }

  // Icon variant - default dropdown trigger
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`relative ${className}`}
          data-testid="theme-toggle"
          aria-label={t(translations.theme)}
        >
          <motion.div
            key={themePreference}
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            {getIcon()}
          </motion.div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-[calc(100vw-32px)] sm:w-40 max-w-[400px] mx-4 sm:mx-0"
        sideOffset={8}
      >
        <DropdownMenuItem 
          onClick={() => setTheme('light')}
          className="flex items-center justify-between cursor-pointer"
          data-testid="theme-light"
        >
          <div className="flex items-center gap-2">
            <Sun className="w-4 h-4" />
            <span>{t(translations.light)}</span>
          </div>
          {themePreference === 'light' && <Check className="w-4 h-4 text-orange-500" />}
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTheme('dark')}
          className="flex items-center justify-between cursor-pointer"
          data-testid="theme-dark"
        >
          <div className="flex items-center gap-2">
            <Moon className="w-4 h-4" />
            <span>{t(translations.dark)}</span>
          </div>
          {themePreference === 'dark' && <Check className="w-4 h-4 text-orange-500" />}
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => setTheme('system')}
          className="flex items-center justify-between cursor-pointer"
          data-testid="theme-system"
        >
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            <span>{t(translations.system)}</span>
          </div>
          {themePreference === 'system' && <Check className="w-4 h-4 text-orange-500" />}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ThemeToggle;
